import { API_BASE_URL, fetchApi, type ApiError } from './api';

const BASE = 'safety-stock';

export const SAFETY_STOCK_ACTIVE_RUN_KEY = 'safety_stock_active_run_id';

export const ABC_XYZ_KEYS = [
  'AX',
  'AY',
  'AZ',
  'BX',
  'BY',
  'BZ',
  'CX',
  'CY',
  'CZ',
] as const;

export type AbcXyzKey = (typeof ABC_XYZ_KEYS)[number];

export type SafetyStockTimeUnit = 'DAY' | 'WEEK' | 'MONTH';
export type SafetyStockServiceMeasure = 'CYCLE_SERVICE_LEVEL' | 'FILL_RATE';

export interface SafetyStockOutliersConfig {
  outlier_method?: string;
  iqr_lower_quantile?: number | null;
  iqr_upper_quantile?: number | null;
  iqr_multiplier?: number | null;
  outlier_action?: string;
  apply_outlier_filter_to_demand?: boolean;
  apply_outlier_filter_to_lead_time?: boolean;
}

export interface SafetyStockRoundingConfig {
  rounding_method?: string;
  rounding_multiple?: number | null;
  minimum_safety_stock?: number | null;
  maximum_safety_stock?: number | null;
  minimum_coverage_days?: number | null;
  maximum_coverage_days?: number | null;
}

export interface SafetyStockFallbackConfig {
  fallback_method?: string;
  fallback_safety_days?: number | null;
  fallback_minimum_safety_stock?: number | null;
  fallback_grouping_level?: string;
  fallback_use_family_distribution?: boolean;
  fallback_use_category_distribution?: boolean;
  fallback_use_supplier_distribution?: boolean;
}

export interface SafetyStockEngineConfig {
  id: number;
  organization: number;
  time_unit: SafetyStockTimeUnit;
  configured_safety_days: string | number;
  maximum_safety_days: string | number;
  minimum_lead_time_observations: number;
  minimum_positive_observations: number;
  number_of_simulations: number;
  random_seed: number;
  adi_threshold: string | number;
  cv_squared_threshold: string | number;
  service_measure: SafetyStockServiceMeasure;
  default_service_level: string | number;
  service_levels: Partial<Record<AbcXyzKey, number>>;
  outliers: SafetyStockOutliersConfig;
  rounding: SafetyStockRoundingConfig;
  fallback: SafetyStockFallbackConfig;
  active_products_only: boolean;
  extra_config: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface SafetyStockConfigResponse {
  created: boolean;
  config: SafetyStockEngineConfig;
  engine_overrides_preview?: Record<string, unknown>;
  config_snapshot?: Record<string, unknown>;
}

export interface SafetyStockScopePreview {
  organization_id: number;
  active_products_only: boolean;
  total_items: number;
  max_items: number;
  exceeds_max: boolean;
  excluded_no_warehouse: number;
  items_built?: number;
  counts_match?: boolean;
  by_warehouse?: Array<{ warehouse_id: number; code?: string | null; name?: string | null; items: number }>;
  by_supplier?: Array<{ supplier_id: number; name?: string | null; items: number }>;
}

export interface SafetyStockEngineHealth {
  status: string;
  engine_version?: string;
  methods?: Record<string, unknown>;
  code?: string;
  message?: string;
}

export interface SafetyStockStartRunResponse {
  status: string;
  accepted: boolean;
  run_id: string;
  organization_id: number;
  total_items: number;
  poll_url: string;
  request_id?: string;
}

export interface SafetyStockBusyResponse {
  code: string;
  message: string;
  severity?: string;
  run_id: string;
  reattach: boolean;
  poll_url?: string | null;
  total_items?: number;
  successful_items?: number;
  failed_items?: number;
}

export interface SafetyStockRunStatus {
  run_id: string;
  organization_id: number;
  status: string;
  mode?: string;
  dry_run?: boolean;
  total_items: number;
  successful_items: number;
  failed_items: number;
  started_at?: string | null;
  finished_at?: string | null;
  error_message?: string | null;
  request_id?: string | null;
  executed_by_id?: number | null;
  summary_ready: boolean;
  summary_building: boolean;
  engine?: {
    status?: string;
    total_items?: number;
    successful_items?: number;
    failed_items?: number;
    error?: { code?: string; message?: string } | null;
  };
  totals?: {
    safety_stock_units: string;
    safety_stock_value: string;
    items: number;
    valued_items: number;
  } | null;
}

export interface SafetyStockRunListItem {
  run_id: string;
  status: string;
  mode?: string;
  dry_run?: boolean;
  source?: string;
  engine_version?: string | null;
  total_items: number;
  successful_items: number;
  failed_items: number;
  started_at?: string | null;
  finished_at?: string | null;
  executed_by_id?: number | null;
  executed_by?: string | null;
  request_id?: string | null;
  summary_ready: boolean;
  safety_stock_units?: string | null;
  safety_stock_value?: string | null;
  coverage_days_median?: string | null;
  delta_value_vs_previous?: string | null;
  previous_run_id?: string | null;
}

export interface SafetyStockRunListResponse {
  page: number;
  page_size: number;
  total: number;
  results: SafetyStockRunListItem[];
}

export interface SafetyStockSummaryRow {
  id?: number | null;
  run?: string | null;
  organization?: number | null;
  dimension?: string;
  dimension_value?: string;
  dimension_label?: string;
  items?: number;
  valued_items?: number;
  items_failed?: number;
  safety_stock_units?: string | number | null;
  safety_stock_value?: string | number | null;
  previous_safety_stock_units?: string | number | null;
  previous_safety_stock_value?: string | number | null;
  protection_demand_units?: string | number | null;
  protection_demand_value?: string | number | null;
  daily_demand_units?: string | number | null;
  daily_demand_value?: string | number | null;
  coverage_days_median?: string | number | null;
  avg_protection_days?: string | number | null;
  avg_service_level?: string | number | null;
  ss_pct_protection_units?: string | number | null;
  ss_pct_protection_value?: string | number | null;
  coverage_days_units?: string | number | null;
  coverage_days_value?: string | number | null;
  delta_units?: string | number | null;
  delta_value?: string | number | null;
  delta_value_pct?: string | number | null;
  no_data_reasons?: Record<string, string>;
  method_mix?: Record<string, number>;
  warning_mix?: Record<string, number>;
  extra?: Record<string, unknown>;
  created_at?: string | null;
}

export interface SafetyStockRunSummaryResponse {
  run_id: string;
  run_status: string;
  summary_ready: boolean;
  abc_basis: string;
  summary: SafetyStockSummaryRow;
  code?: string;
  message?: string;
  notice?: string;
}

export interface SafetyStockCurrentSummaryResponse {
  source: string;
  has_engine_run: boolean;
  latest_run_id: string | null;
  latest_run_status: string | null;
  abc_basis: string;
  notice: string | null;
  summary: SafetyStockSummaryRow;
}

export interface SafetyStockBreakdownResponse {
  run_id: string;
  dimension: string;
  abc_basis: string;
  enabled_cat_fields: string[];
  rows: SafetyStockSummaryRow[];
}

export interface SafetyStockRunErrorsResponse {
  run_id: string;
  page: number;
  page_size: number;
  total: number;
  results: Array<{
    id: number;
    product_id?: number;
    product_code?: string | null;
    product_name?: string | null;
    supplier_id?: number;
    warehouse_id?: number;
    status?: string;
    error_code?: string | null;
    error_message?: string | null;
    warnings?: unknown;
  }>;
}

export interface SafetyStockCompareRow {
  dimension_value: string;
  dimension_label: string;
  run_a_value?: string | null;
  run_b_value?: string | null;
  delta_value?: string | null;
  run_a_coverage_days?: string | null;
  run_b_coverage_days?: string | null;
  delta_coverage_days?: string | null;
  run_a_items?: number;
  run_b_items?: number;
}

export interface SafetyStockRunsCompareResponse {
  run_a_id: string;
  run_b_id: string;
  run_a_started_at?: string | null;
  run_b_started_at?: string | null;
  dimension: string;
  abc_basis: string;
  notice?: string;
  enabled_cat_fields?: string[];
  rows: SafetyStockCompareRow[];
}

export interface SafetyStockSimulateSampleResponse {
  estimation: boolean;
  sample_size_requested: number;
  sample_size: number;
  population_size: number;
  engine_status?: string;
  successful_items?: number;
  failed_items?: number;
  run_id?: string;
  request_id?: string;
  metrics: Record<string, unknown>;
  notice?: string;
}

export interface SafetyStockPolicyRow {
  id: number;
  product_id: number;
  product_code?: string | null;
  product_name?: string | null;
  family?: string | null;
  family_id?: number | null;
  warehouse_id: number;
  warehouse_code?: string | null;
  warehouse_name?: string | null;
  supplier_id: number;
  supplier_name?: string | null;
  product_supplier_id?: number;
  abc_class?: string;
  xyz_class?: string;
  abc_xyz?: string;
  demand_classification?: string;
  method?: string;
  service_measure?: string;
  service_level?: string | number;
  safety_stock?: string | number;
  unit_cost?: string | number | null;
  safety_stock_value?: string | number | null;
  coverage_days?: string | number | null;
  recent_total?: number | null;
  protection_period_days?: string | number | null;
  lead_time?: string | number | null;
  review_period?: string | number | null;
  expected_protection_demand?: string | number | null;
  update_type?: string;
  last_calculation_id?: string | null;
  last_run_id?: string | null;
  synced_to_product_supplier?: boolean;
  has_warnings?: boolean;
  updated_at?: string | null;
  created_at?: string | null;
}

export interface SafetyStockPoliciesResponse {
  page: number;
  page_size: number;
  total: number;
  sort: string;
  sort_dir: string;
  results: SafetyStockPolicyRow[];
  recent_period_label?: string | null;
  enabled_cat_fields?: string[];
}

export interface SafetyStockFilterOption {
  value: string | number;
  label: string;
}

export interface SafetyStockPolicyFiltersResponse {
  warehouses: SafetyStockFilterOption[];
  suppliers: SafetyStockFilterOption[];
  families: SafetyStockFilterOption[];
  abc_xyz: SafetyStockFilterOption[];
  demand_classifications: SafetyStockFilterOption[];
  methods: SafetyStockFilterOption[];
  update_types: SafetyStockFilterOption[];
  has_warnings?: SafetyStockFilterOption[];
  enabled_cat_fields?: string[];
}

export interface SafetyStockSkuActionBody {
  product_id: number;
  warehouse_id: number;
  supplier_id: number;
  overrides?: Record<string, unknown>;
  return_trace?: boolean;
}

export interface SafetyStockPreviewResponse {
  estimation: boolean;
  persisted: boolean;
  current_safety_stock: string | null;
  preview_safety_stock: number | string | null;
  engine: Record<string, unknown>;
}

export interface SafetyStockManualBody {
  product_id: number;
  warehouse_id: number;
  supplier_id: number;
  safety_stock: number;
  reason: string;
  product_supplier_id?: number | null;
}

export interface SafetyStockBacktestBody {
  product_id: number;
  warehouse_id: number;
  supplier_id: number;
  persist?: boolean;
  overrides?: Record<string, unknown>;
  candidate_methods?: string[] | null;
}

export type StartOrgRecalculateResult =
  | { kind: 'accepted'; data: SafetyStockStartRunResponse }
  | { kind: 'reattach'; data: SafetyStockBusyResponse }
  | { kind: 'forbidden'; message: string; code?: string }
  | { kind: 'error'; status: number; message: string; code?: string };

function authHeaders(): Headers {
  const headers = new Headers({ 'Content-Type': 'application/json' });
  const token = localStorage.getItem('auth_token');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

async function postRunsRaw(warehouseId: number): Promise<StartOrgRecalculateResult> {
  const response = await fetch(`${API_BASE_URL}/${BASE}/runs/`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ warehouse_id: warehouseId }),
  });
  let data: Record<string, unknown> = {};
  try {
    data = (await response.json()) as Record<string, unknown>;
  } catch {
    data = {};
  }

  if (response.status === 202 || response.status === 200) {
    return { kind: 'accepted', data: data as unknown as SafetyStockStartRunResponse };
  }
  if (response.status === 409 && data.reattach && data.run_id) {
    return { kind: 'reattach', data: data as unknown as SafetyStockBusyResponse };
  }
  if (response.status === 403) {
    return {
      kind: 'forbidden',
      message: String(data.message || 'Apply is disabled.'),
      code: typeof data.code === 'string' ? data.code : undefined,
    };
  }
  return {
    kind: 'error',
    status: response.status,
    message: String(data.message || data.detail || 'Failed to start recalculation.'),
    code: typeof data.code === 'string' ? data.code : undefined,
  };
}

export const safetyStockService = {
  getHealth(options?: { signal?: AbortSignal }) {
    return fetchApi<SafetyStockEngineHealth>(`${BASE}/engine/health/`, {
      signal: options?.signal,
    });
  },

  getConfig(options?: { signal?: AbortSignal }) {
    return fetchApi<SafetyStockConfigResponse>(`${BASE}/config/`, {
      signal: options?.signal,
    });
  },

  updateConfig(payload: Partial<SafetyStockEngineConfig>) {
    return fetchApi<SafetyStockConfigResponse>(`${BASE}/config/`, {
      method: 'PUT',
      body: payload,
    });
  },

  resetConfig() {
    return fetchApi<SafetyStockConfigResponse>(`${BASE}/config/reset/`, {
      method: 'POST',
      body: {},
    });
  },

  previewScope(
    params?: { active_products_only?: boolean; warehouse_id?: number },
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<SafetyStockScopePreview>(`${BASE}/scope/preview/`, {
      params: {
        ...(params?.active_products_only == null
          ? {}
          : { active_products_only: params.active_products_only }),
        ...(params?.warehouse_id != null ? { warehouse_id: params.warehouse_id } : {}),
      },
      signal: options?.signal,
    });
  },

  listRuns(
    params?: { page?: number; page_size?: number; warehouse_id?: number },
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<SafetyStockRunListResponse>(`${BASE}/runs/`, {
      params: {
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 20,
        ...(params?.warehouse_id != null ? { warehouse_id: params.warehouse_id } : {}),
      },
      signal: options?.signal,
    });
  },

  getRun(runId: string, options?: { signal?: AbortSignal }) {
    return fetchApi<SafetyStockRunStatus>(`${BASE}/runs/${runId}/`, {
      signal: options?.signal,
    });
  },

  getRunSummary(
    runId: string,
    params?: { warehouse_id?: number },
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<SafetyStockRunSummaryResponse>(`${BASE}/runs/${runId}/summary/`, {
      params: params?.warehouse_id != null ? { warehouse_id: params.warehouse_id } : undefined,
      signal: options?.signal,
    });
  },

  getCurrentSummary(
    params?: { warehouse_id?: number },
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<SafetyStockCurrentSummaryResponse>(`${BASE}/summary/current/`, {
      params: params?.warehouse_id != null ? { warehouse_id: params.warehouse_id } : undefined,
      signal: options?.signal,
    });
  },

  getRunBreakdown(
    runId: string,
    dimension = 'ABC_XYZ',
    params?: { warehouse_id?: number },
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<SafetyStockBreakdownResponse>(`${BASE}/runs/${runId}/breakdown/`, {
      params: {
        dimension,
        ...(params?.warehouse_id != null ? { warehouse_id: params.warehouse_id } : {}),
      },
      signal: options?.signal,
    });
  },

  getRunErrors(
    runId: string,
    params?: { page?: number; page_size?: number; warehouse_id?: number },
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<SafetyStockRunErrorsResponse>(`${BASE}/runs/${runId}/errors/`, {
      params: {
        page: params?.page ?? 1,
        page_size: params?.page_size ?? 20,
        ...(params?.warehouse_id != null ? { warehouse_id: params.warehouse_id } : {}),
      },
      signal: options?.signal,
    });
  },

  compareRuns(
    params: {
      run_a: string;
      run_b: string;
      dimension?: string;
      warehouse_id?: number;
    },
    options?: { signal?: AbortSignal },
  ) {
    return fetchApi<SafetyStockRunsCompareResponse>(`${BASE}/runs/compare/`, {
      params: {
        run_a: params.run_a,
        run_b: params.run_b,
        dimension: params.dimension || 'ABC_XYZ',
        ...(params.warehouse_id != null ? { warehouse_id: params.warehouse_id } : {}),
      },
      signal: options?.signal,
    });
  },

  startOrgRecalculate(warehouseId: number) {
    return postRunsRaw(warehouseId);
  },

  simulateSample(sampleSize = 200, warehouseId?: number) {
    return fetchApi<SafetyStockSimulateSampleResponse>(`${BASE}/simulate/sample/`, {
      method: 'POST',
      body: {
        sample_size: sampleSize,
        ...(warehouseId != null ? { warehouse_id: warehouseId } : {}),
      },
    });
  },

  listPolicies(params?: Record<string, string | number | boolean | string[] | number[]>, options?: { signal?: AbortSignal }) {
    return fetchApi<SafetyStockPoliciesResponse>(`${BASE}/policies/`, {
      params,
      signal: options?.signal,
    });
  },

  getPolicyFilters(options?: { signal?: AbortSignal }) {
    return fetchApi<SafetyStockPolicyFiltersResponse>(`${BASE}/policies/filters/`, {
      signal: options?.signal,
    });
  },

  validatePolicy(body: SafetyStockSkuActionBody) {
    return fetchApi<Record<string, unknown>>(`${BASE}/policies/validate/`, {
      method: 'POST',
      body,
    });
  },

  previewPolicy(body: SafetyStockSkuActionBody) {
    return fetchApi<SafetyStockPreviewResponse>(`${BASE}/policies/preview/`, {
      method: 'POST',
      body,
    });
  },

  applyPolicy(body: SafetyStockSkuActionBody) {
    return fetchApi<Record<string, unknown>>(`${BASE}/policies/apply/`, {
      method: 'POST',
      body,
    });
  },

  manualPolicy(body: SafetyStockManualBody) {
    return fetchApi<Record<string, unknown>>(`${BASE}/policies/manual/`, {
      method: 'PATCH',
      body,
    });
  },

  backtestPolicy(body: SafetyStockBacktestBody) {
    return fetchApi<Record<string, unknown>>(`${BASE}/policies/backtest/`, {
      method: 'POST',
      body,
    });
  },
};

export function asApiError(error: unknown): ApiError {
  if (error && typeof error === 'object' && 'status' in error) {
    return error as ApiError;
  }
  return {
    status: 0,
    message: error instanceof Error ? error.message : 'Unknown error',
  };
}

export function persistActiveRunId(runId: string | null) {
  if (!runId) {
    localStorage.removeItem(SAFETY_STOCK_ACTIVE_RUN_KEY);
    return;
  }
  localStorage.setItem(SAFETY_STOCK_ACTIVE_RUN_KEY, runId);
}

export function readActiveRunId(): string | null {
  return localStorage.getItem(SAFETY_STOCK_ACTIVE_RUN_KEY);
}
